import {Component, ElementRef, Input, OnInit, Output, ViewChild} from '@angular/core';
import {Subject} from 'rxjs';
import {BsModalRef} from 'ngx-bootstrap/modal';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {BusinessProcess} from '../../../../Models/businessProcess';
import {BusinessProcessService} from '../../../../services/business-process.service';
import {FormsValidatorService} from '../../../../services/forms-validator.service';
import {HttpEventType} from '@angular/common/http';
import {Utils} from '../../../../services/utils';
import {GenericModalService} from '../../../../services/generic-modal.service';

@Component({
  selector: 'app-import',
  templateUrl: './import.component.html',
  styleUrls: ['./import.component.css', '../../../../styles/modals.css']
})
export class ImportComponent implements OnInit {

  @ViewChild('lblFile', { static: false }) file: ElementRef;
  fileToUpload: File = null;
  fileUploadText = 'Selecione o arquivo';

  @Input() modalTitle: string;
  @Output() itemSaved: Subject<boolean>;

  optScopeReplaceChecked = true;
  optScopeNewChecked = false;
  availableBP: BusinessProcess[];

  importForm: FormGroup;
  submitClick = false;
  successMessage = '';
  errorMessage = '';
  pleaseWaitMessage = '';

  get formData() { return this.importForm.controls; }

  constructor(public bsModalRef: BsModalRef, private formBuilder: FormBuilder, private bpService: BusinessProcessService,
              public modalService: GenericModalService) { }

  ngOnInit() {
    this.itemSaved = new Subject();
    this.createForm();
    this.loadAllBusinessProcesses();
  }

  loadAllBusinessProcesses() {
    this.bpService.getAllBusinessProcesses().subscribe(res => {
      this.availableBP = res.sort((a, b) => a.name.localeCompare(b.name));
    }, error1 => {
      let err = error1;
      if (error1.indexOf('401') !== -1) {
        err = 'Erro: Sessão expirada.';
      }
      this.errorMessage = err;
    });
  }

  createForm() {
    this.importForm = this.formBuilder.group({
      scopeReplace: ['', Validators.required],
      scopeNew: ['', Validators.maxLength(100)],
      fileBP: ['', Validators.compose([ Validators.required ])]
    });
  }

  onFileChange(files: FileList) {
    if (files.length > 0) {
      const ext = files[0].name.split('.')[1];
      // somente se a extensão for .bp, deixar continuar.
      if (ext === 'bp') {
        this.file.nativeElement.innerText = Array.from(files).map(r => r.name).join(', ');
        this.fileToUpload = files.item(0);
      }
    }
  }

  clearSelectedFile() {
    this.file.nativeElement.innerText = this.fileUploadText;
    this.fileToUpload = null;
    this.importForm.get('fileBP').patchValue('');
  }

  onImport() {
    this.enableFields(false);
    let bpName = '';
    const pleaseWaitLayout = '<span><i class="fas fa-clock"></i> []</span>';

    this.errorMessage = '';

    if (this.optScopeReplaceChecked) {
      bpName = this.availableBP
        .filter(f => f.businessProcessId === this.importForm.controls.scopeReplace.value)
        .map(m => m.name)[0];
    } else {
      bpName = this.importForm.controls.scopeNew.value;
    }

    this.pleaseWaitMessage = pleaseWaitLayout.replace('[]', 'Upload de arquivo. Por favor, aguarde');
    this.bpService.uploadBusinessProcess(this.fileToUpload)
      .subscribe(data => {
          if (data.type === HttpEventType.UploadProgress) {
            console.log('upload in progress');
          }
          if (data.type === HttpEventType.Response) {
            console.log('upload complete');
          }
        },
          error1 => {
            this.enableFields(true);
            this.setErrorMessage(error1);
            this.pleaseWaitMessage = '';
          },
        () => {
          this.pleaseWaitMessage = pleaseWaitLayout.replace('[]', 'Importando processo de negócio');
          this.importBusinessProcess(bpName);
      });
  }

  importBusinessProcess(bpName){
    this.bpService.importBusinessProcess(bpName).subscribe(imp => {
        if (imp.type === HttpEventType.UploadProgress) {
          console.log('import in progress');
        }
        if (imp.type === HttpEventType.Response) {
          console.log('import complete');
          this.enableFields(true);
          this.pleaseWaitMessage = '';
          this.successMessage = 'Processo de Negócio importado com sucesso!';
          this.itemSaved.next(true);
          setTimeout(() => {
            this.successMessage = '';
            this.bsModalRef.hide();
          }, 2000);
        }
      },
      error1 =>  {
        this.pleaseWaitMessage = '';
        this.enableFields(true);
        let err = error1;
        if (error1.indexOf('401') !== -1) {
          err = 'Erro: Sessão expirada.';
        }
        this.errorMessage = err;
        console.error(error1);
      });
  }

  enableFields(enable){
    if (enable){
      this.importForm.get('scopeNew').enable();
      this.importForm.get('scopeReplace').enable();
      this.importForm.get('fileBP').enable();
    } else {
      this.importForm.get('scopeNew').disable();
      this.importForm.get('scopeReplace').disable();
      this.importForm.get('fileBP').disable();
    }
  }

  setErrorMessage(err) {
    if (err.indexOf('401') !== -1) {
      err = 'Erro: Sessão expirada.';
    }
    this.pleaseWaitMessage = '';
    this.errorMessage = err;
    console.error(err);
  }

  optClick(element) {
    if (element.target.id.indexOf('Replace') > -1) {
      this.optScopeReplaceChecked = true;
      this.optScopeNewChecked = false;
      this.importForm.controls['scopeReplace'].setValidators([Validators.required]);
      FormsValidatorService.removeValidator( this.importForm, 'scopeNew');
    } else if (element.target.id.indexOf('New') > -1) {
      this.optScopeReplaceChecked = false;
      this.optScopeNewChecked = true;
      this.importForm.controls['scopeNew'].setValidators([ Validators.required ]);
      FormsValidatorService.removeValidator( this.importForm, 'scopeReplace');
    }
    this.importForm.get('scopeNew').updateValueAndValidity();
    this.importForm.get('scopeReplace').updateValueAndValidity();
  }

  validateScopeNew(element) {
    const value = element.target.value;
    // remover os espaços a mais e atualizar no form
    Utils.adjustSpaces(value, this.importForm, 'scopeNew');

    const allBp = this.availableBP.map(m => m.name.toLowerCase());
    if (allBp.indexOf(value.toLowerCase()) > -1) {
      this.importForm.controls['scopeNew'].setErrors({ isValidScopeNew: true });
    } else {
      this.importForm.controls['scopeNew'].setErrors(null);
    }
  }

  onClose() {
    this.itemSaved.next(false);
    this.bsModalRef.hide();
  }

}
